import React from 'react';
import './AboutPage.css';

const AboutPage = () => {
  return (
    <div className="about-page">
      <header>
        <div className="nav-inner">
          <div className="brand">
            <div className="brand-logo" aria-hidden="true"></div>
            <div>
              <div className="brand-text-main">Anicha Earth</div>
              <div className="brand-text-sub">Satellite timelines</div>
            </div>
          </div>
          <nav>
            <div className="nav-links">
              <a href="#product">Product</a>
              <a href="#use-cases">Use Cases</a>
              <a href="#technology">Technology</a>
              <a href="#roadmap">Roadmap</a>
              <a href="#team">Team</a>
            </div>
            <a href="https://anicha.earth" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
              <span className="btn-pill-label">Demo app</span>
            </a>
          </nav>
        </div>
      </header>

      <main className="page">
        <section id="hero">
          <div className="hero-inner">
            <div>
              <div className="eyebrow">
                <span className="eyebrow-dot"></span>
                AI-first satellite intelligence platform
              </div>
              <h1 className="hero-title">
                Watch any place on Earth
                <span className="hero-highlight">change over time</span>
              </h1>
              <p className="hero-subtitle">
                Anicha Earth turns global satellite archives into an interactive timeline for climate,
                infrastructure, and land-use monitoring. Pick a place, scrub through years of imagery,
                and zoom into <b>AI-enhanced detail</b> — directly in the browser or through our APIs.
              </p>
              <div className="hero-cta-row">
                <a href="https://anicha.earth" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                  <span className="btn-pill-label">Demo app</span>
                </a>
                <a
                  href="mailto:konstantin@anicha.earth?subject=Anicha%20API%20Access&body=Hi%20Anicha%20Earth%20team%2C%0A%0AI%27d%20like%20to%20request%20access%20to%20your%20API.%0A%0AUse%20case%3A%0A-%20%0A%0AThanks%21"
                  className="btn btn-ghost"
                >
                  <span className="btn-pill-label">Request API access</span>
                </a>
              </div>
              <div className="hero-meta">
                <span>Digital-native startup offering its own web app, API & platform</span>
                <span>Global Sentinel-2 archive · AI super-resolution</span>
              </div>
            </div>
          </div>
        </section>

        <section id="about">
          <div className="section-header">
            <div className="section-eyebrow">Why we exist</div>
            <h2 className="section-title">Satellite data without the PhD barrier</h2>
            <p className="section-subtitle">
              The world’s surface is changing faster than ever — cities expand, forests burn, rivers move,
              crops fail. We make that change visible and explorable for teams that need answers, not
              raw pixels.
            </p>
          </div>
          <div className="about-grid">
            <article className="about-card">
              <h3>The problem</h3>
              <p>
                Missions like Sentinel-2 capture the Earth every few days, but the data lives in
                specialist tools and complex archives. For most climate, infrastructure, and risk teams,
                that means:
              </p>
              <ul className="about-list">
                <li>Slow, ad-hoc access to historical imagery.</li>
                <li>Fragmented workflows between analysts, decision-makers, and developers.</li>
                <li>High effort to go from “satellite scene” to “actionable insight”.</li>
              </ul>
            </article>
            <article className="about-card">
              <h3>Our solution</h3>
              <p>
                Anicha Earth is a digital-native startup that makes global satellite archives feel like a
                scrub-through video:
              </p>
              <ul className="about-list">
                <li>
                  A simple web app where anyone can select an area and scroll through years of imagery.
                </li>
                <li>
                  AI-enhanced views that sharpen Sentinel-2 data toward ~1–2 m effective detail.
                </li>
                <li>
                  A cloud-native API layer so developers can integrate “time-lapse Earth” into their own
                  products.
                </li>
              </ul>
              <p>
                Our target users are climate and land-use teams, infrastructure planners, insurers,
                researchers, and startups who need satellite insight without becoming remote-sensing
                experts.
              </p>
            </article>
          </div>
        </section>

        <section id="product">
          <div className="section-header">
            <div className="section-eyebrow">Products</div>
            <h2 className="section-title">From web app to full platform</h2>
            <p className="section-subtitle">
              Anicha Earth offers a public Explorer app today and is building out APIs and alerts for
              teams that need to monitor places at scale.
            </p>
          </div>
          <div className="product-grid">
            <article className="product-card">
              <div className="product-label-row">
                <div className="product-name">Anicha Explorer</div>
                <div className="badge">
                  <span className="badge-dot"></span>
                  Public beta
                </div>
              </div>
              <div className="product-body">
                A fast, no-frills web app to explore how any location on Earth has changed since 2018.
                Draw a shape or drop a point, then scrub through every available satellite scene. Switch
                between raw and AI-enhanced imagery to inspect detail like field boundaries,
                construction, deforestation, flooding, and more.
              </div>
              <div className="product-metadata">
                <span><span className="product-dot"></span><strong>Target users:</strong> Analysts,
                  climate &amp; land-use teams, researchers, journalists, curious humans.</span>
                <span><span className="product-dot"></span><strong>Current stage:</strong> Live as a
                  public beta at <a href="https://anicha.earth" target="_blank" rel="noopener noreferrer">anicha.earth</a>.</span>
              </div>
            </article>

            <article className="product-card">
              <div className="product-label-row">
                <div className="product-name">Anicha API</div>
                <div className="badge badge-secondary">
                  <span className="badge-dot"></span>
                  Private alpha
                </div>
              </div>
              <div className="product-body">
                A developer-friendly API to programmatically query imagery for any area, generate
                timelapse stacks, and access AI-enhanced tiles. Designed for integration into dashboards,
                internal tools, and data pipelines.
                <ul className="about-list" style={{ marginTop: '0.4rem' }}>
                  <li>Search: list scenes for a geometry and date range.</li>
                  <li>Tiles: serve map tiles (raw &amp; AI-enhanced).</li>
                  <li>Timelapse: generate ready-to-embed animations.</li>
                </ul>
              </div>
              <div className="product-metadata">
                <span><span className="product-dot"></span><strong>Target users:</strong> Product teams,
                  GIS developers, climate &amp; infra startups, research labs.</span>
                <span><span className="product-dot"></span><strong>Current stage:</strong> Internal use;
                  inviting early design partners for private alpha.</span>
              </div>
            </article>

            <article className="product-card">
              <div className="product-label-row">
                <div className="product-name">Anicha Alerts</div>
                <div className="badge badge-danger">
                  <span className="badge-dot"></span>
                  On roadmap
                </div>
              </div>
              <div className="product-body">
                Workspace and alerting for teams that need to know when a place changes. Define areas of
                interest and thresholds, then receive notifications when new imagery suggests
                deforestation, flooding, construction, or other change.
              </div>
              <div className="product-metadata">
                <span><span className="product-dot"></span><strong>Target users:</strong> Climate &amp;
                  nature-based projects, infrastructure operators, insurers, NGOs.</span>
                <span><span className="product-dot"></span><strong>Current stage:</strong> In design &
                  prototyping; pilots planned after API general availability.</span>
              </div>
            </article>
          </div>
        </section>

        <section id="use-cases">
          <div className="section-header">
            <div className="section-eyebrow">Use cases</div>
            <h2 className="section-title">What people use Anicha for</h2>
            <p className="section-subtitle">
              Anicha Earth is built for teams working in climate, agriculture, infrastructure, and risk
              who need a living record of how places evolve.
            </p>
          </div>
          <div className="usecase-grid">
            <article className="usecase-card">
              <div className="usecase-icon">🌍</div>
              <div>
                <div className="usecase-title">Climate &amp; land-use change</div>
                <p className="usecase-text">
                  Track deforestation, wetland loss, coastal erosion, and urban sprawl over multiple
                  years. Support climate reporting and nature-based solutions with visual, map-based
                  evidence.
                </p>
              </div>
            </article>
            <article className="usecase-card">
              <div className="usecase-icon">🏙️</div>
              <div>
                <div className="usecase-title">Urban development monitoring</div>
                <p className="usecase-text">
                  Observe how cities grow and densify over time. Monitor new buildings, expansions, and
                  changes to surrounding land so planners, investors, and communities can understand the
                  long-term impact of urban development.
                </p>
              </div>
            </article>
            <article className="usecase-card">
              <div className="usecase-icon">🏗️</div>
              <div>
                <div className="usecase-title">Infrastructure &amp; construction</div>
                <p className="usecase-text">
                  Monitor construction progress, new roads, and changing surroundings without drones or
                  site visits. Quickly answer: “What did this site look like last year? Five years ago?”
                </p>
              </div>
            </article>
            <article className="usecase-card">
              <div className="usecase-icon">⚠️</div>
              <div>
                <div className="usecase-title">Disaster &amp; risk monitoring</div>
                <p className="usecase-text">
                  Compare before-and-after views around floods, storms, and wildfires. Support insurers,
                  responders, and risk teams with a consistent satellite record.
                </p>
              </div>
            </article>
          </div>
        </section>

        <section id="technology">
          <div className="section-header">
            <div className="section-eyebrow">Technology</div>
            <h2 className="section-title">Built on open EO data and AI</h2>
            <p className="section-subtitle">
              Under the hood, Anicha Earth combines public Earth-observation data with custom
              super-resolution models to make satellite timelines both powerful and approachable.
            </p>
          </div>
          <article className="tech-card">
            <p>
              We stand on the shoulders of missions like Copernicus Sentinel-2 and add an intelligence
              layer focused on usability, performance, and developer access.
            </p>
            <ul className="tech-list">
              <li>
                Copernicus Sentinel-2 L2A archive with ~10 m native resolution and ~5-day revisit,
                available globally since 2018.
              </li>
              <li>
                AI super-resolution models that push imagery towards ~1–2 m effective detail, trained on
                millions of satellite and aerial images.
              </li>
              <li>
                Model heritage from large-scale agricultural super-resolution work, bringing Sentinel-2
                data down to field-level detail for real-world decisions.
              </li>
              <li>
                Cloud-native architecture designed for platforms such as Google Cloud, enabling
                scalable processing, storage, and API access.
              </li>
            </ul>
          </article>
        </section>

        <section id="roadmap">
          <div className="section-header">
            <div className="section-eyebrow">Roadmap</div>
            <h2 className="section-title">Shipping toward a full platform</h2>
            <p className="section-subtitle">
              Anicha Earth is already live as a web app and is evolving into a full geospatial analytics
              platform with APIs, workspaces, and alerts.
            </p>
          </div>
          <div className="roadmap">
            <div className="roadmap-item">
              <div className="roadmap-marker">
                <div className="roadmap-dot"></div>
              </div>
              <div className="roadmap-content">
                <h3>2025</h3>
                <h4>Public Explorer beta</h4>
                <p>
                  Launch of Anicha Explorer at anicha.earth with global Sentinel-2 timelines and
                  AI-enhanced imagery for any point on Earth.
                </p>
              </div>
            </div>
            <div className="roadmap-item">
              <div className="roadmap-marker">
                <div className="roadmap-dot"></div>
              </div>
              <div className="roadmap-content">
                <h3>2026 · H1</h3>
                <h4>Developer API</h4>
                <p>
                  Release of Anicha API for programmatic access to imagery, tiles, and timelapse stacks.
                  Early pilots with climate, infrastructure, and ag-tech partners.
                </p>
              </div>
            </div>
            <div className="roadmap-item">
              <div className="roadmap-marker">
                <div className="roadmap-dot"></div>
              </div>
              <div className="roadmap-content">
                <h3>2026 · H2</h3>
                <h4>Alerts &amp; workspaces</h4>
                <p>
                  Team workspaces, saved projects, and change-detection alerts with integrations into
                  collaboration tools (Slack, email, webhooks).
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="team">
          <div className="section-header">
            <div className="section-eyebrow">Team</div>
            <h2 className="section-title">Founding team</h2>
            <p className="section-subtitle">
              Anicha Earth is founded by a small team with deep experience in satellite data, AI, and
              building products for real-world users.
            </p>
          </div>
          <div className="team-grid">
            <article className="team-card">
              <div className="team-photo">
                <img
                  src="/varik.jpeg"
                  alt="Portrait of Konstantin Varik"
                />
              </div>
              <div className="team-name">Konstantin Varik</div>
              <div className="team-role">Chief Scientist</div>
              <p className="team-bio">
                Konstantin is a co-founder of DigiFarm and has led the development of deep-learning
                models for satellite super-resolution and crop prediction across millions of hectares
                worldwide. At Anicha Earth he drives the AI and geospatial science behind the platform.
              </p>
            </article>

            <article className="team-card">
              <div className="team-photo">
                <img
                  src="/tania.jpeg"
                  alt="Portrait of Tatiana Darch"
                />
              </div>
              <div className="team-name">Tatiana Darch</div>
              <div className="team-role">PR &amp; GR</div>
              <p className="team-bio">
                Tatiana leads communications, partnerships, and government relations. She helps
                translate complex geospatial technology into clear stories and works with institutions,
                programs, and the wider ecosystem to bring Anicha Earth into the right conversations.
              </p>
            </article>

            <article className="team-card">
              <div className="team-photo">
                <img
                  src="/nils.jpeg"
                  alt="Portrait of Nils Helset"
                />
              </div>
              <div className="team-name">Nils Helset</div>
              <div className="team-role">Management</div>
              <p className="team-bio">
                Nils is a 15th-generation farmer and co-founder &amp; CEO of DigiFarm. He has spent
                years applying satellite data and AI to real agricultural problems, from field boundary
                detection to yield optimisation. At Anicha Earth he focuses on strategy, operations, and
                turning technology into a sustainable business.
              </p>
            </article>
          </div>
        </section>

        <section id="contact">
          <div className="contact-card">
            <div>
              <h2 className="contact-title">Let’s build with Earth data</h2>
              <p className="contact-text">
                We’re actively working with early users and partners who need better visibility into
                how places evolve over time — from climate and nature projects to infrastructure,
                insurance, and research.
              </p>
              <p className="contact-meta">
                Email: <a href="mailto:konstantin@anicha.earth">konstantin@anicha.earth</a><br />
                Anicha Earth is a remote-first, digital-native startup building its own cloud-hosted
                geospatial analytics platform.
              </p>
            </div>
            <div className="contact-ctas">
              <a href="https://anicha.earth" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                <span className="btn-pill-label">Demo app</span>
              </a>
              <a
                href="mailto:konstantin@anicha.earth?subject=Anicha%20API%20Access&body=Hi%20Anicha%20Earth%20team%2C%0A%0AI%27d%20like%20to%20request%20access%20to%20your%20API.%0A%0AUse%20case%3A%0A-%20%0A%0AThanks%21"
                className="btn btn-ghost"
              >
                <span className="btn-pill-label">Request early API access</span>
              </a>
            </div>
          </div>
        </section>

        <footer>
          <div className="footer-inner">
            <div>
              © 2025 Anicha Earth · All rights reserved.<br />
              Anicha Earth is a tech startup offering its own web app, API, and platform for
              satellite-based change monitoring.
            </div>
            <div className="footer-links">
              <a href="#hero">Back to top</a>
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default AboutPage;
